import { useState } from "react"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Button } from "@components/ui/button"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useAuthUserDetails } from "@apis/users/useUsers"
import { useGetRetentionOffersQuery } from "@apis/retention/useRetention"
import type { IOffer } from "@commonTypes/offer"
import { useDropOfferMutation } from "@apis/accounts/useAccounts"
import { OfferPromoAndLoyaltyProgramSection } from "./Utils"

type TPendingCancellationFormProps = {
  account_id: string
  params?: {
    description?: string
  }
  onSave?: () => void
}

export const PendingCancellationForm = ({
  account_id,
  params,
  onSave,
}: TPendingCancellationFormProps) => {
  const [selectedOffer, setSelectedOffer] = useState<IOffer | null>(null)

  const { data: userInfo } = useAuthUserDetails()
  const {
    data: offers,
    isPending,
    isError,
    error,
  } = useGetRetentionOffersQuery(account_id, {
    user_id: userInfo?.user_id,
  })

  const dropOfferMutation = useDropOfferMutation()

  const handleSelectOffer = (offer_code: string) => {
    const offer = offers?.eligible_offers.find(
      (item) => item.offer_code === offer_code,
    )
    setSelectedOffer(offer ?? null)
  }

  const handleChangeOffer = () => {
    if (!selectedOffer || !userInfo?.email) return

    dropOfferMutation.mutate(
      {
        account_id: account_id,
        offer_code: selectedOffer.offer_code,
        request: { audit: { user_name: userInfo.email } },
      },
      {
        onSuccess: () => {
          toast.success("Succesfully changed offer!")
          onSave?.()
        },
        onError: (error) => toast.error(`Error changing offer: ${error}`),
      },
    )
  }

  if (isPending)
    return (
      <div className="tw:flex tw:items-center tw:gap-2">
        <Loader2 className="tw:h-5 tw:w-5 tw:animate-spin" />
        <span>Loading retention offers…</span>
      </div>
    )

  if (isError) {
    return (
      <div className="tw:text-sm tw:text-red-500">
        Error loading retention offers: {String(error)}
      </div>
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <p
        dangerouslySetInnerHTML={{ __html: params?.description as string }}
      ></p>
      <div className="tw:w-1/2">
        <label className="tw:text-sm tw:font-medium">
          Change Account's Offer
        </label>
        <Select
          onValueChange={handleSelectOffer}
          value={selectedOffer?.offer_code ?? ""}
        >
          <SelectTrigger className="tw:mt-1 tw:w-full">
            <SelectValue placeholder="Keep current offer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Keep current offer</SelectItem>
            {offers?.eligible_offers &&
              offers.eligible_offers.length > 0 &&
              offers.eligible_offers.map((offer: IOffer) => (
                <SelectItem key={offer.offer_code} value={offer.offer_code}>
                  {offer.offer_desc} - {offer.rate_friendly}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {selectedOffer && (
        <>
          <OfferPromoAndLoyaltyProgramSection
            title="Promos:"
            items={selectedOffer.plan_promos}
            getLabel={(promo) => promo.promo_desc}
          />
          <OfferPromoAndLoyaltyProgramSection
            title="Loyalty Programs:"
            items={selectedOffer.plan_loyalty_programs}
            getLabel={(lp) => lp.loyalty_program_desc}
          />
        </>
      )}
      <div className="tw:flex tw:justify-start">
        <Button
          onClick={handleChangeOffer}
          disabled={!selectedOffer || dropOfferMutation.isPending}
        >
          {dropOfferMutation.isPending ? (
            <>
              <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
              Changing Offer…
            </>
          ) : (
            "Change Offer"
          )}
        </Button>
      </div>
    </div>
  )
}
